import {
  Alert,
  Badge,
  Box,
  Button,
  Group,
  Paper,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import {
  IconAlertCircle,
  IconAlertTriangle,
  IconArrowLeft,
} from "@tabler/icons-react";
import type React from "react";
import { useParams } from "wouter";
import { client } from "@/api-client";
import { FancyLoader } from "@/components/FancyLoader";
import { Header } from "@/components/Header";
import type {
  QuestionType,
  SurveyDefinition,
  SurveyFilledOutput,
  SurveyStatus,
} from "@/utils/survey";

const getStatusColor = (status: SurveyStatus) => {
  switch (status) {
    case "FILLED":
      return "green";
    case "PARTIALLY_FILLED":
      return "orange";
    case "NOT_FILLED":
      return "gray";
    default:
      return "blue";
  }
};

const getStatusLabel = (status: SurveyStatus) => {
  switch (status) {
    case "FILLED":
      return "Completed";
    case "PARTIALLY_FILLED":
      return "In Progress";
    case "NOT_FILLED":
      return "Not Started";
    default:
      return "Unknown";
  }
};

export interface SurveyViewerProps {
  survey: SurveyFilledOutput;
}

export const SurveyViewer = ({ survey }: SurveyViewerProps) => {
  const { definition, values, status } = survey;

  const renderValue = (
    questionId: string,
    type: QuestionType,
    _options?: string[],
  ) => {
    const value = values[questionId];

    if (value === undefined || value === null) {
      return (
        <Text td="italic" c="dimmed">
          Not answered
        </Text>
      );
    }

    switch (type) {
      case "YES_NO":
        return <Text>{value === true ? "Yes" : "No"}</Text>;

      case "SHORT_TEXT":
      case "LONG_TEXT":
        return <Text>{value as string}</Text>;

      case "MULTIPLE_CHOICE":
        return <Text>{value as string}</Text>;

      case "MULTIPLE_CHOICE_MULTI": {
        const selectedOptions = value as string[];
        if (selectedOptions.length === 0) {
          return (
            <Text td="italic" c="dimmed">
              None selected
            </Text>
          );
        }
        return (
          <Stack gap="xs">
            {selectedOptions.map((option, index) => (
              <Text key={index}>• {option}</Text>
            ))}
          </Stack>
        );
      }

      default:
        return <Text c="red">Unsupported question type</Text>;
    }
  };

  return (
    <Stack>
      <Group>
        <Title order={3}>Survey Results</Title>
        <Badge color={getStatusColor(status)}>{getStatusLabel(status)}</Badge>
      </Group>

      {definition.questions.map((question) => (
        <Paper key={question.id} withBorder p="md" radius="md">
          <Stack>
            <Group>
              <Text fw={500}>
                Q: {question.label}
                {question.required && (
                  <Text span color="red">
                    {" "}
                    (required)
                  </Text>
                )}
              </Text>
            </Group>

            {question.description && (
              <Text size="sm" c="dimmed">
                {question.description}
              </Text>
            )}

            <Box mt={5}>
              {renderValue(question.id, question.type, question.options ?? [])}
            </Box>
          </Stack>
        </Paper>
      ))}

      <Group>
        <Text size="sm" c="dimmed">
          Survey Version: {new Date(survey.definition.version).toLocaleString()}
        </Text>
      </Group>
    </Stack>
  );
};

export const SurveyResult: React.FC = () => {
  const { engagementId, surveyId } = useParams<{
    engagementId: string;
    surveyId: string;
  }>();

  // Get the survey file
  const { data: surveyFile, isLoading: isLoadingSurveyFile } = client.useQuery(
    "get",
    "/universal/files/{fileId}",
    {
      params: {
        path: {
          fileId: surveyId,
        },
      },
    },
    {
      staleTime: 0,
    },
  );

  // Get the filled survey
  const { data: filledSurvey, isLoading: isLoadingFilledSurvey } =
    client.useQuery(
      "get",
      "/trainer/engagements/{engagementId}/surveys/{surveyId}/filled",
      {
        params: {
          path: {
            engagementId: engagementId,
            surveyId: surveyId,
          },
        },
      },
    );

  const isLoading = isLoadingSurveyFile || isLoadingFilledSurvey;

  const handleBackToSession = () => {
    window.history.back();
  };

  // Parse survey file content and check for version mismatch
  const surveyDefinition = surveyFile?.content
    ? (JSON.parse(surveyFile.content) as SurveyDefinition)
    : null;
  const hasVersionMismatch =
    surveyDefinition &&
    filledSurvey &&
    new Date(surveyDefinition.version).getTime() !==
      new Date(filledSurvey.definition.version).getTime();

  if (isLoading) {
    return <FancyLoader />;
  }

  if (!surveyFile || !surveyFile.content) {
    return (
      <Stack>
        <Header title="Survey Results" />
        <Alert color="red" title="Error" icon={<IconAlertCircle />}>
          Survey not found or could not be loaded.
        </Alert>
        <Button
          leftSection={<IconArrowLeft size={14} />}
          onClick={handleBackToSession}
        >
          Back
        </Button>
      </Stack>
    );
  }

  if (!filledSurvey) {
    return (
      <Stack>
        <Header title={surveyFile.name} />
        <Alert color="orange" title="Not Filled" icon={<IconAlertCircle />}>
          This survey has not been filled out by the client yet.
        </Alert>
        <Button
          leftSection={<IconArrowLeft size={14} />}
          onClick={handleBackToSession}
        >
          Back
        </Button>
      </Stack>
    );
  }

  return (
    <Stack>
      <Header title={surveyFile.name} />

      {hasVersionMismatch && (
        <Alert
          color="yellow"
          title="The survey was updated"
          icon={<IconAlertTriangle />}
        >
          You changed the survey after this response was submitted. The content
          shown may be outdated.
        </Alert>
      )}

      <SurveyViewer survey={filledSurvey} />
    </Stack>
  );
};
